// Admin v2 — közös UI-elemek a Figma „Admin (web)” oldal (ADM_*) tokenjei szerint.
// Tokenek: háttér #010306, oldalsáv #0b0f14, kártya #15191E, mező #1d232a, keret #31383D,
// cián #00AFF2, arany #d9a514, lila #a855f7, zöld #2bc48a, piros #ef4444, másodlagos szöveg #B8B8C3, halvány #8a8f99.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Icon } from './icons';

export const C = {
  bg: '#010306',
  side: '#0b0f14',
  card: '#15191E',
  field: '#1d232a',
  border: '#31383D',
  cyan: '#00AFF2',
  gold: '#d9a514',
  purple: '#a855f7',
  green: '#2bc48a',
  red: '#ef4444',
  text2: '#B8B8C3',
  muted: '#8a8f99',
};

export type Tone = 'cyan' | 'gold' | 'purple' | 'green' | 'red' | 'gray';
export const TONE: Record<Tone, string> = {
  cyan: C.cyan, gold: C.gold, purple: C.purple, green: C.green, red: C.red, gray: C.muted,
};

/* ---------- oldal-fejléc ---------- */
export function PageHeader({ title, sub, right }: { title: string; sub?: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div className="min-w-0">
        <h1 className="text-[28px] leading-[34px] font-extrabold italic uppercase text-white">{title}</h1>
        {sub && <div className="mt-1.5 text-[14px] text-[#B8B8C3]">{sub}</div>}
      </div>
      {right && <div className="flex items-center gap-3 shrink-0 mt-2">{right}</div>}
    </div>
  );
}

export function SectionTitle({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="text-[15px] font-extrabold italic uppercase text-white tracking-[0.01em]">{children}</h2>
      {right}
    </div>
  );
}

/* ---------- kártyák ---------- */
export function Card({ children, className = '', pad = true }: { children: React.ReactNode; className?: string; pad?: boolean }) {
  return (
    <div className={`bg-[#15191E] border border-[#31383D] rounded-2xl ${pad ? 'p-5' : ''} ${className}`}>{children}</div>
  );
}

export function IconTile({ icon, tone = 'cyan', size = 36, iconSize = 20 }: { icon: string; tone?: Tone; size?: number; iconSize?: number }) {
  const c = TONE[tone];
  return (
    <span
      className="inline-flex items-center justify-center rounded-[10px] shrink-0"
      style={{ width: size, height: size, background: `${c}24`, color: c }}
    >
      <Icon name={icon} size={iconSize} />
    </span>
  );
}

export function StatCard({ icon, tone = 'cyan', label, value, delta, valueColor }: {
  icon: string; tone?: Tone; label: string; value: React.ReactNode;
  delta?: { text: string; up: boolean } | null; valueColor?: string;
}) {
  return (
    <Card className="!p-[21px] min-h-[122px]">
      <div className="flex items-center gap-2.5">
        <IconTile icon={icon} tone={tone} />
        <span className="text-[13px] font-medium text-[#B8B8C3]">{label}</span>
      </div>
      <div className="mt-2.5 text-[28px] leading-[34px] font-bold" style={{ color: valueColor ?? '#fff' }}>{value}</div>
      {delta && (
        <div className="mt-2 flex items-center gap-1 text-[12px]" style={{ color: delta.up ? C.green : C.red }}>
          <Icon name={delta.up ? 'ArrowUp' : 'ArrowDown'} size={12} />
          {delta.text}
        </div>
      )}
    </Card>
  );
}

/* ---------- jelvény / szerep ---------- */
export function Badge({ tone = 'cyan', children }: { tone?: Tone; children: React.ReactNode }) {
  const c = TONE[tone];
  return (
    <span
      className="inline-flex items-center h-[23px] px-2.5 rounded-full text-[12px] font-semibold whitespace-nowrap"
      style={{ background: `${c}24`, color: tone === 'gray' ? C.text2 : c }}
    >
      {children}
    </span>
  );
}

/* ---------- avatar ---------- */
export function Avatar({ src, name, size = 34 }: { src?: string | null; name?: string; size?: number }) {
  const [ok, setOk] = useState(Boolean(src));
  useEffect(() => setOk(Boolean(src)), [src]);
  const initials = (name || '').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-[#1d232a] border border-[#31383D] overflow-hidden shrink-0 text-[#8a8f99] font-semibold"
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.34) }}
    >
      {ok && src ? <img src={src} alt="" className="w-full h-full object-cover" onError={() => setOk(false)} /> : initials}
    </span>
  );
}

/* ---------- gombok ---------- */
type BtnKind = 'primary' | 'ghost' | 'danger';
export function Button({ kind = 'primary', icon, children, className = '', ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: BtnKind; icon?: string }) {
  const k = {
    primary: 'bg-[#00AFF2] text-white hover:brightness-110',
    ghost: 'bg-[#1d232a] text-white border border-[#31383D] hover:border-[#00AFF2]/50',
    danger: 'bg-[#ef4444] text-white hover:brightness-110',
  }[kind];
  return (
    <button
      type="button"
      {...rest}
      className={`inline-flex items-center justify-center gap-2 h-[37px] px-[18px] rounded-full text-[14px] font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${k} ${className}`}
    >
      {icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
}

export const IconButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { icon: string; size?: number }>(
  ({ icon, size = 34, className = '', ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={`inline-flex items-center justify-center rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#B8B8C3] hover:text-white hover:border-[#00AFF2]/50 transition ${className}`}
      style={{ width: size, height: size }}
    >
      <Icon name={icon} size={16} />
    </button>
  ),
);

/* ---------- szegmentált választó ---------- */
export function Segmented<T extends string>({ value, options, onChange, size = 'md' }: {
  value: T; options: { value: T; label: string; disabled?: boolean; title?: string }[]; onChange: (v: T) => void; size?: 'sm' | 'md';
}) {
  const h = size === 'sm' ? 'h-[28px] text-[12px] px-3' : 'h-[32px] text-[13px] px-4';
  return (
    <div className="inline-flex items-center gap-1 p-[4px] rounded-full bg-[#15191E] border border-[#31383D]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={o.disabled}
          title={o.title}
          aria-disabled={o.disabled || undefined}
          onClick={() => { if (!o.disabled) onChange(o.value); }}
          className={`${h} rounded-full font-semibold whitespace-nowrap transition ${o.value === value ? 'bg-[#00AFF2] text-white' : o.disabled ? 'text-[#B8B8C3] opacity-40 cursor-not-allowed' : 'text-[#B8B8C3] hover:text-white'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- fülek ---------- */
export function Tabs<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex items-center gap-1 border-b border-[#31383D] mb-6 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`relative px-4 h-[42px] text-[13px] font-medium whitespace-nowrap transition ${o.value === value ? 'text-[#00AFF2]' : 'text-[#B8B8C3] hover:text-white'}`}
        >
          {o.label}
          {o.value === value && <span className="absolute left-2 right-2 -bottom-px h-[2px] bg-[#00AFF2] rounded-full" />}
        </button>
      ))}
    </div>
  );
}

/* ---------- űrlapmezők ---------- */
export function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[13px] font-medium text-[#B8B8C3] mb-2">{label}</span>
      {children}
    </label>
  );
}

export function Input({ icon, right, className = '', ...rest }: React.InputHTMLAttributes<HTMLInputElement> & { icon?: string; right?: React.ReactNode }) {
  return (
    <div className={`flex items-center gap-2.5 h-[44px] px-[15px] rounded-xl bg-[#1d232a] border border-[#31383D] focus-within:border-[#00AFF2]/70 transition ${className}`}>
      {icon && <Icon name={icon} size={18} className="text-[#8a8f99]" />}
      <input {...rest} className="flex-1 min-w-0 bg-transparent outline-none text-[14px] text-white placeholder:text-[#8a8f99]" />
      {right}
    </div>
  );
}

export function PasswordInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false);
  return (
    <Input
      {...props}
      icon="Lock"
      type={show ? 'text' : 'password'}
      right={
        <button type="button" onClick={() => setShow((s) => !s)} className="text-[#8a8f99] hover:text-white" aria-label={show ? 'Jelszó elrejtése' : 'Jelszó mutatása'}>
          <Icon name={show ? 'EyeSlash' : 'Eye'} size={18} />
        </button>
      }
    />
  );
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className="w-full min-h-[56px] px-[15px] py-3 rounded-xl bg-[#1d232a] border border-[#31383D] focus:border-[#00AFF2]/70 outline-none text-[14px] text-white placeholder:text-[#8a8f99] resize-y"
    />
  );
}

export function Select<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="relative h-[44px]">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="appearance-none w-full h-full pl-[15px] pr-10 rounded-xl bg-[#1d232a] border border-[#31383D] focus:border-[#00AFF2]/70 outline-none text-[14px] text-white"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <Icon name="CaretDown" size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#B8B8C3] pointer-events-none" />
    </div>
  );
}

export function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-[44px] h-[24px] rounded-full transition ${checked ? 'bg-[#00AFF2]' : 'bg-[#31383D]'}`}
    >
      <span className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-all ${checked ? 'left-[23px]' : 'left-[3px]'}`} />
    </button>
  );
}

export function Notice({ tone = 'gold', children }: { tone?: Tone; children: React.ReactNode }) {
  const c = TONE[tone];
  return (
    <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-[13px]" style={{ background: `${c}14`, border: `1px solid ${c}66`, color: c }}>
      <Icon name="Warning" size={18} />
      <span>{children}</span>
    </div>
  );
}

/* ---------- modális ablak ---------- */
export function Modal({ title, onClose, children, footer, width = 560 }: {
  title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; width?: number;
}) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-[2px]" onMouseDown={onClose}>
      <div
        className="w-full max-h-[92vh] overflow-y-auto bg-[#15191E] border border-[#31383D] rounded-2xl p-7 shadow-2xl"
        style={{ maxWidth: width }}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-extrabold italic uppercase text-white">{title}</h3>
          <button type="button" onClick={onClose} className="text-[#B8B8C3] hover:text-white" aria-label="Bezárás">
            <Icon name="X" size={22} />
          </button>
        </div>
        {children}
        {footer && <div className="flex items-center justify-end gap-3 mt-6">{footer}</div>}
      </div>
    </div>
  );
}

export function ConfirmModal({ title, text, confirmLabel, danger, onConfirm, onClose }: {
  title: string; text: React.ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => Promise<void> | void; onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title={title}
      onClose={onClose}
      width={460}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Mégse</Button>
          <Button
            kind={danger ? 'danger' : 'primary'}
            disabled={busy}
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-[14px] text-[#B8B8C3] leading-relaxed">{text}</p>
    </Modal>
  );
}

/* ---------- lebegő menü (⋮) ---------- */
export type MenuItem = { icon: string; label: string; onClick: () => void; danger?: boolean; hidden?: boolean };
export function ActionMenu({ items, trigger = 'DotsThreeVertical' }: { items: MenuItem[]; trigger?: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btn = useRef<HTMLButtonElement>(null);
  const visible = items.filter((i) => !i.hidden);
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const r = btn.current?.getBoundingClientRect();
    if (r) {
      const h = visible.length * 34 + 12;
      const top = r.bottom + 6 + h > window.innerHeight ? r.top - h - 6 : r.bottom + 6;
      setPos({ top, left: Math.max(8, r.right - 260) });
    }
    setOpen((o) => !o);
  };
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);
  return (
    <>
      <IconButton ref={btn} icon={trigger} onClick={toggle} aria-label="Műveletek" />
      {open && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div
            className="fixed z-[56] w-[260px] p-1.5 rounded-xl bg-[#15191E] border border-[#31383D] shadow-2xl"
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {visible.map((i) => (
              <button
                key={i.label}
                type="button"
                onClick={() => { setOpen(false); i.onClick(); }}
                className={`w-full flex items-center gap-2.5 h-[34px] px-3 rounded-lg text-[13px] text-left transition hover:bg-white/5 ${i.danger ? 'text-[#ef4444]' : 'text-white'}`}
              >
                <Icon name={i.icon} size={16} className={i.danger ? '' : 'text-[#B8B8C3]'} />
                {i.label}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ---------- értesítés (toast) ---------- */
type ToastMsg = { id: number; text: string; tone: Tone };
const ToastCtx = createContext<(text: string, tone?: Tone) => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [list, setList] = useState<ToastMsg[]>([]);
  const push = useCallback((text: string, tone: Tone = 'green') => {
    const id = Date.now() + Math.random();
    setList((l) => [...l, { id, text, tone }]);
    window.setTimeout(() => setList((l) => l.filter((x) => x.id !== id)), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] flex flex-col items-center gap-2 pointer-events-none">
        {list.map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-[#15191E] border text-[13px] text-white shadow-2xl"
            style={{ borderColor: `${TONE[t.tone]}80` }}
          >
            <Icon name={t.tone === 'red' ? 'Warning' : 'CheckCircle'} size={18} style={{ color: TONE[t.tone] }} />
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ---------- üres / töltés állapot ---------- */
export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-10 text-center text-[14px] text-[#8a8f99]">{children}</div>;
}

export function Spinner() {
  return (
    <div className="py-16 flex justify-center">
      <span className="w-8 h-8 rounded-full border-2 border-[#31383D] border-t-[#00AFF2] animate-spin" />
    </div>
  );
}

/* ---------- lapozó ---------- */
export function Pagination({ page, total, pageSize, onChange }: { page: number; total: number; pageSize: number; onChange: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  // legfeljebb 5 oldalszám a jelenlegi körül
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const nums = Array.from({ length: Math.min(5, pages) }, (_, i) => start + i);
  const btn = 'h-9 min-w-[36px] px-3 rounded-full text-[13px] font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
      <span className="text-[12.5px] text-[#8a8f99]">{from}–{to} / {total}</span>
      <div className="flex items-center gap-1.5">
        <button type="button" aria-label="Előző oldal" className={`${btn} bg-[#15191E] border border-[#31383D] text-white flex items-center`} disabled={page <= 1} onClick={() => onChange(page - 1)}>
          <Icon name="CaretLeft" size={14} />
        </button>
        {nums.map((n) => (
          <button key={n} type="button" aria-current={n === page ? 'page' : undefined} onClick={() => onChange(n)}
            className={`${btn} ${n === page ? 'bg-[#00AFF2] text-white' : 'bg-[#15191E] border border-[#31383D] text-[#B8B8C3] hover:text-white'}`}>{n}</button>
        ))}
        <button type="button" aria-label="Következő oldal" className={`${btn} bg-[#15191E] border border-[#31383D] text-white flex items-center`} disabled={page >= pages} onClick={() => onChange(page + 1)}>
          <Icon name="CaretRight" size={14} />
        </button>
      </div>
    </div>
  );
}
